/**
 * Turning a run of `LiveMetrics` samples into charts.
 *
 * The port of `LiveChartBuilder`. Three rules are load-bearing and each is the difference between a
 * chart that tells the truth and one that merely looks plausible:
 *
 *   * **THE WINDOW ENDS AT THE NEWEST SAMPLE'S OWN TIMESTAMP** - the agent's clock, not the
 *     browser's. Skew between the two is ordinary (a machine whose NTP has drifted, a VM that was
 *     suspended), and anchoring to `Date.now()` would slide every graph sideways by that drift, or
 *     empty them entirely when the device's clock reads ahead.
 *   * **A fixed ceiling where one is natural, autoscale otherwise** - 100 for a percentage, the
 *     window's peak for bytes per second. An autoscaled axis with no number anywhere near it is a
 *     shape rather than a measurement, so the peak is printed in the chart's own header.
 *   * **A series with nothing in the window is still a series.** A GPU or a sensor that appeared
 *     anywhere in the window gets a chart for the whole window, so a chart does not appear and
 *     disappear as the machine's inventory of sensors wobbles.
 *
 * It is a module of its own rather than part of the Performance tab because the same samples are
 * charted from two sources - the agent's rolling 30-minute buffer and the server's stored 24-hour
 * maintenance history - and the two must be drawn identically or they cannot be compared.
 */
import type { LiveMetrics } from './contracts.gen.js';
import type { ChartSpec } from './ui/ChartCard.js';
import type { Series } from './ui/Sparkline.js';

/** The live window: what the agent keeps in memory. */
export const LIVE_WINDOW_MS = 30 * 60 * 1000;
/** The maintenance window: what the server stores at one sample a minute. */
export const MAINTENANCE_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * The charts for `samples`, oldest first.
 *
 * Empty when there is nothing in the window, which the caller renders as its waiting line - an empty
 * set of axes says "this machine reports nothing", which is a different and much stronger claim than
 * "no sample has arrived yet".
 */
export function buildCharts(
  samples: readonly LiveMetrics[],
  windowMs: number
): ChartSpec[] {
  if (samples.length === 0) return [];

  const sorted = [...samples].sort((a, b) => Date.parse(a.capturedUtc) - Date.parse(b.capturedUtc));
  const end = Date.parse(sorted[sorted.length - 1]!.capturedUtc);
  const start = end - windowMs;
  const inWindow = sorted.filter(s => Date.parse(s.capturedUtc) >= start);
  if (inWindow.length === 0) return [];

  const at = inWindow.map(s => Date.parse(s.capturedUtc));
  const latest = inWindow[inWindow.length - 1]!;
  const charts: ChartSpec[] = [];

  charts.push({
    title: 'CPU',
    current: `${Math.round(latest.cpuPercent)}%`,
    max: 100,
    format: pct,
    series: [{ label: 'CPU', colorIndex: 0, at, points: inWindow.map(s => s.cpuPercent) }]
  });

  charts.push({
    title: 'Memory',
    current:
      latest.memoryTotalBytes > 0
        ? `${gb(latest.memoryUsedBytes)} of ${gb(latest.memoryTotalBytes)} (${Math.round(
            percent(latest.memoryUsedBytes, latest.memoryTotalBytes)
          )}%)`
        : '—',
    max: 100,
    format: v => `${pct(v)} used`,
    series: [
      {
        label: 'Memory',
        colorIndex: 0,
        at,
        points: inWindow.map(s => percent(s.memoryUsedBytes, s.memoryTotalBytes))
      }
    ]
  });

  // One chart per adapter name seen anywhere in the window.
  for (const name of distinct(inWindow.flatMap(s => (s.gpus ?? []).map(g => g.name)))) {
    const current = (latest.gpus ?? []).find(g => g.name === name);
    charts.push({
      title: name,
      current: !current
        ? '—'
        : current.memoryTotalBytes > 0
          ? `${Math.round(current.utilizationPercent)}% · ${gb(current.memoryUsedBytes)} of ${gb(
              current.memoryTotalBytes
            )}`
          : `${Math.round(current.utilizationPercent)}%`,
      max: 100,
      format: pct,
      series: [
        {
          label: name,
          colorIndex: 0,
          ...sample(inWindow, s => (s.gpus ?? []).find(g => g.name === name)?.utilizationPercent)
        }
      ]
    });
  }

  // Temperatures: one chart, one series per sensor, the legend carrying each sensor's current value.
  const sensors = distinct(inWindow.flatMap(s => (s.temperatures ?? []).map(t => t.name)));
  if (sensors.length > 0) {
    const hottest = Math.max(
      0,
      ...inWindow.flatMap(s => (s.temperatures ?? []).map(t => t.celsius))
    );
    // 100 °C is the floor of the scale, not the ceiling: a machine idling at 40° should not draw a
    // chart that looks alarming, and one running at 105° must not draw off the top.
    const scale = Math.max(100, hottest);
    // The header carries the reading that matters now - the hottest sensor's current value - and
    // says which sensor it is when there is more than one. The scale was all it used to show, which
    // left the live temperature nowhere on the page but an unreadable legend.
    const now = latest.temperatures ?? [];
    const hottestNow = now.reduce<(typeof now)[number] | null>(
      (best, t) => (best === null || t.celsius > best.celsius ? t : best),
      null
    );
    charts.push({
      title: 'Temperatures',
      current: !hottestNow
        ? `scale 0–${Math.round(scale)} °C`
        : now.length === 1
          ? `${round1(hottestNow.celsius)} °C`
          : `${round1(hottestNow.celsius)} °C hottest (${hottestNow.name})`,
      max: scale,
      format: v => `${round1(v)} °C`,
      series: sensors.map((name, i) => ({
        label: name,
        colorIndex: i,
        ...sample(inWindow, s => (s.temperatures ?? []).find(t => t.name === name)?.celsius)
      })),
      legend: sensors.map((name, i) => {
        const current = (latest.temperatures ?? []).find(t => t.name === name);
        return {
          text: current ? `${name} ${round1(current.celsius)} °C` : name,
          colorIndex: i
        };
      })
    });
  }

  // Network: two series against one autoscaled axis, with the peak named in the legend.
  {
    const peak = Math.max(
      10 * 1024,
      ...inWindow.map(s => Math.max(s.networkRxBytesPerSec, s.networkTxBytesPerSec))
    );
    charts.push({
      title: 'Network',
      current: `↓ ${rate(latest.networkRxBytesPerSec)}  ↑ ${rate(latest.networkTxBytesPerSec)}`,
      max: peak,
      format: rate,
      series: [
        { label: 'received', colorIndex: 0, at, points: inWindow.map(s => s.networkRxBytesPerSec) },
        { label: 'sent', colorIndex: 1, at, points: inWindow.map(s => s.networkTxBytesPerSec) }
      ],
      legend: [
        { text: '↓ received', colorIndex: 0 },
        { text: '↑ sent', colorIndex: 1 },
        { text: `peak ${rate(peak)}`, colorIndex: 5 }
      ]
    });
  }

  // Disks: percent used per volume. Near-flat by design - a disk that is filling up is the story.
  const volumes = distinct(inWindow.flatMap(s => (s.disks ?? []).map(d => d.name)));
  if (volumes.length > 0) {
    charts.push({
      title: 'Disk usage',
      current: '',
      max: 100,
      format: v => `${pct(v)} used`,
      series: volumes.map((name, i) => ({
        label: name,
        colorIndex: i,
        ...sample(inWindow, s => {
          const disk = (s.disks ?? []).find(d => d.name === name);
          return disk ? percent(disk.totalBytes - disk.freeBytes, disk.totalBytes) : undefined;
        })
      })),
      legend: volumes.map((name, i) => {
        const current = (latest.disks ?? []).find(d => d.name === name);
        return {
          text: current ? `${name} ${gb(current.freeBytes)} free of ${gb(current.totalBytes)}` : name,
          colorIndex: i
        };
      })
    });
  }

  return charts;
}

/** "↓ 1.2 MB/s   ↑ 340 KB/s   ·   up 3 d 4 h   ·   182 processes", the line under the charts. */
export function summaryLine(latest: LiveMetrics | null): string {
  if (!latest) return '';
  return [
    `↓ ${rate(latest.networkRxBytesPerSec)}  ↑ ${rate(latest.networkTxBytesPerSec)}`,
    `up ${uptime(latest.uptimeSeconds)}`,
    `${latest.processCount} processes`
  ].join('   ·   ');
}

/**
 * Pulls one reading per sample, dropping the samples that do not carry it.
 *
 * The timestamps are dropped with them, which is what keeps a sensor that appeared half way through
 * the window from being drawn as if its first reading were the window's start.
 */
function sample(
  samples: readonly LiveMetrics[],
  read: (s: LiveMetrics) => number | undefined
): Pick<Series, 'points' | 'at'> {
  const points: number[] = [];
  const at: number[] = [];
  for (const s of samples) {
    const value = read(s);
    if (value === undefined) continue;
    points.push(value);
    at.push(Date.parse(s.capturedUtc));
  }
  return { points, at };
}

function distinct(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function percent(part: number, whole: number): number {
  return whole <= 0 ? 0 : (100 * part) / whole;
}

function pct(value: number): string {
  return `${Math.round(value)}%`;
}

function round1(value: number): string {
  return (Math.round(value * 10) / 10).toString();
}

/**
 * Sizes in GB or TB, never smaller.
 *
 * Deliberately not `format.bytes`, which picks the best unit per value: these numbers sit beside
 * each other ("14.2 GB of 31.4 GB"), and a pair rendered in two different units is arithmetic the
 * reader has to do in their head.
 */
function gb(value: number): string {
  const GB = 1024 ** 3;
  const TB = GB * 1024;
  return value >= TB ? `${round1(value / TB)} TB` : `${round1(value / GB)} GB`;
}

function rate(bytesPerSecond: number): string {
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;
  if (bytesPerSecond >= GB) return `${round1(bytesPerSecond / GB)} GB/s`;
  if (bytesPerSecond >= MB) return `${round1(bytesPerSecond / MB)} MB/s`;
  return `${round1(bytesPerSecond / KB)} KB/s`;
}

/** "3 d 4 h" / "4 h 12 m" / "12 m". Days matter on a server; seconds never do. */
export function uptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days} d ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} m`;
  return `${minutes} m`;
}
